import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, BarChart3, Megaphone, Wand2, User, PhoneCall } from 'lucide-react';
import { useAuth } from '@/lib/useAuth';
import { apiRequest } from '@/lib/api';

interface DashboardCard {
  path: string;
  label: string;
  description: string;
  icon: typeof Phone;
  capability?: string;
}

const cards: DashboardCard[] = [
  {
    path: '/calls',
    label: 'Calls',
    description: 'View call history, transcripts, and recordings',
    icon: Phone,
    capability: 'calls',
  },
  {
    path: '/insights',
    label: 'Insights',
    description: 'Call metrics, trends, and analytics',
    icon: BarChart3,
    capability: 'insights',
  },
  {
    path: '/my-agent',
    label: 'My Agent',
    description: 'Customize your AI agent settings',
    icon: User,
    capability: 'my_agent',
  },
  {
    path: '/campaigns',
    label: 'Campaigns',
    description: 'Manage outbound calling campaigns',
    icon: Megaphone,
    capability: 'campaigns',
  },
  {
    path: '/phone-numbers',
    label: 'Phone Numbers',
    description: 'Manage and provision phone numbers',
    icon: PhoneCall,
    capability: 'phone_numbers',
  },
  {
    path: '/studio',
    label: 'Studio',
    description: 'Configure and manage your AI agents',
    icon: Wand2,
    capability: 'studio',
  },
];

interface UsageData {
  minutes_used: number;
  plan_minutes_allowance: number;
  remaining: number;
  overage: number;
  overage_rate: number;
  overage_cost: number;
  usage_percent: number;
  plan: string;
  billing_cycle_start: string | null;
}

function UsageWidget() {
  const [usage, setUsage] = useState<UsageData | null>(null);

  useEffect(() => {
    apiRequest<UsageData>('/billing/usage')
      .then(setUsage)
      .catch(() => {}); // No billing = no widget
  }, []);

  if (!usage) return null;

  const hasLimit = usage.plan_minutes_allowance > 0;
  const pct = hasLimit ? Math.min(usage.usage_percent, 100) : 0;
  const isOverage = hasLimit && usage.overage > 0;
  const cycleEnd = usage.billing_cycle_start
    ? new Date(new Date(usage.billing_cycle_start).getTime() + 30 * 24 * 60 * 60 * 1000)
        .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;

  return (
    <div className="rounded-xl border bg-card p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Minutes Used</h2>
        {usage.plan ? (
          <span className="text-sm text-muted-foreground capitalize">{usage.plan} plan</span>
        ) : (
          <span className="text-sm text-muted-foreground">No plan limit</span>
        )}
      </div>

      {/* Progress bar */}
      <div className="w-full h-3 rounded-full bg-muted overflow-hidden mb-3">
        <div
          className={`h-full rounded-full transition-all ${
            isOverage ? 'bg-amber-500' : pct >= 80 ? 'bg-amber-400' : 'bg-primary'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-sm">
        {hasLimit ? (
          <span className="font-medium text-foreground">
            {usage.minutes_used} / {usage.plan_minutes_allowance}
          </span>
        ) : (
          <span className="font-medium text-foreground">
            {usage.minutes_used} minutes
          </span>
        )}
        {isOverage ? (
          <span className="text-amber-500 font-medium">
            {usage.overage} overage min (${usage.overage_cost.toFixed(2)})
          </span>
        ) : hasLimit ? (
          <span className="text-muted-foreground">
            {usage.remaining} remaining
          </span>
        ) : null}
      </div>

      {cycleEnd && (
        <p className="text-xs text-muted-foreground mt-2">
          Cycle resets {cycleEnd}
        </p>
      )}
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { hasCap, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  const visibleCards = cards.filter((c) => !c.capability || hasCap(c.capability));

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Dashboard</h1>

      {/* Usage widget — only shows for customers with a plan */}
      <UsageWidget />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visibleCards.map((card) => {
          const Icon = card.icon;
          return (
            <button
              key={card.path}
              onClick={() => navigate(card.path)}
              className="flex flex-col items-start gap-3 rounded-xl border bg-card p-6 text-left shadow-sm transition-all hover:shadow-md hover:border-primary/50"
            >
              <div className="rounded-lg bg-primary/10 p-3">
                <Icon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">{card.label}</h2>
                <p className="text-sm text-muted-foreground">{card.description}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
